// Surveys.tsx
import React from 'react'
import { InboxTab } from '../home/inboxes'
import { SimpleUser } from '../../providers/context'
import { useRequest } from '../../request'
import { Loader } from '../../components/simple/loader'
import { Button } from '../../components/simple/button'
import { useSmartLinkTo } from '../../components/simple/link'
import { DateWrapper } from '../../components/simple/date'
import { MessageBase } from '../home/message'
import { MiniPie, MiniPieData } from '../../components/charts/miniPie'

type Survey = {
  id: number
  name: string
  expiration_date?: string
  schoolName?: string
  siblingsInSameSchool?: Array<{ id: number; fname: string; lname: string }>
  created_at?: string
  date?: string
  pie?: MiniPieData // Available for all user types now
}

type SurveyApiResponse = { currentSurveys: Survey[]; pastSurveys: Survey[] }

interface SurveysProps {
  user: SimpleUser
  inboxTab: { tab: InboxTab }
}

export const Surveys: React.FC<SurveysProps> = ({ user, inboxTab }) => {
  const { data: surveys } = useRequest<SurveyApiResponse>('/form/getAllSurveys', inboxTab.tab)

  const linkTo = useSmartLinkTo()

  if (!surveys) {
    return <Loader />
  }
  // Destructure for convenience
  const { currentSurveys, pastSurveys } = surveys

  return (
    <div className="flex-grow overflow-y-auto overflow-x-hidden bg-slate-50 px-[.375rem]">
      {/* Header */}
      <div className="mb-2 mt-3 flex justify-center gap-3 text-center">
        <b className="text-lg">Your Forms</b>
        {user.type === 'teacher' && (
          <Button logo small onClick={linkTo('/form/create')}>
            New Form
          </Button>
        )}
      </div>
      {currentSurveys.length > 0 ? (
        <>
          <div className="mb-2 mt-3 text-center text-lg font-bold">Current Forms</div>
          <div>
            {currentSurveys.map((survey) => (
              <SurveyRow key={survey.id} survey={survey} user={user} />
            ))}
          </div>
        </>
      ) : (
        <div className="mb-4 text-center italic text-gray-500">No active forms at the moment.</div>
      )}

      {/* Past Surveys */}
      {pastSurveys.length > 0 && (
        <>
          <div className="mb-2 mt-3 text-center text-lg font-bold">Form History</div>
          <div className="opacity-75">
            {pastSurveys.map((survey) => (
              <SurveyRow key={survey.id} survey={survey} user={user} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

const SurveyRow: React.FC<{ survey: Survey; user: SimpleUser }> = ({ survey, user }) => {
  const linkTo = useSmartLinkTo()
  const validSiblings = survey.siblingsInSameSchool || []

  const targetUrl = user.type === 'teacher' ? `/form/dashboard/${survey.id}` : `/form/fill/${survey.id}`

  return (
    <MessageBase
      onClick={linkTo(targetUrl)}
      avatar={{ color: '#2295f0', img: 'icon-survey' }}
      top={
        <>
          <div className="flex-1 font-bold">{survey.name}</div>
          {survey.created_at && (
            <div className="ml-auto flex-shrink-0 font-semibold opacity-60">
              <DateWrapper detailed date={survey.created_at} />
            </div>
          )}
        </>
      }
      bottom={
        <>
          {survey.schoolName && <div className="text-sm font-bold opacity-50">{survey.schoolName}</div>}
          {user.type == 'teacher' && survey.expiration_date && (
            <div className="ml-auto text-sm font-bold opacity-50">until {survey.expiration_date}</div>
          )}
          {validSiblings.length > 0 && (
            <div className="text-sm text-gray-600">
              {validSiblings.length === 1 ? 'Student: ' : 'Students: '}
              {validSiblings.map((sibling, index) => (
                <React.Fragment key={sibling.id}>
                  {sibling.fname} {sibling.lname}
                  {index < validSiblings.length - 1 ? ', ' : ''}
                </React.Fragment>
              ))}
            </div>
          )}
          {user.type === 'teacher' && survey.pie && <MiniPie className="ml-auto" data={survey.pie} />}
          {(user.type === 'guardian' || user.type === 'student') && survey.pie && (
            <MiniPie className="ml-auto" data={survey.pie} />
          )}
        </>
      }
    />
  )
}
